import { Box, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import React from 'react'
import { MdCheckCircle, MdError, MdHourglassEmpty } from 'react-icons/md'
import { UploadedFile } from './UploadStep'

export type ProcessingStatus = 'pending' | 'processing' | 'done' | 'error'

interface ProcessingStepProps {
  files: UploadedFile[]
  statuses: Record<number, ProcessingStatus>
}

export const ProcessingStep: React.FC<ProcessingStepProps> = ({ files, statuses }) => {
  const completed = Object.values(statuses).filter(s => s === 'done' || s === 'error').length
  const progress = files.length > 0 ? (completed / files.length) * 100 : 0

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Extracting certificate data...
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          AI is analyzing {files.length} certificate{files.length !== 1 ? 's' : ''}. This may take a moment.
        </Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, height: 8, borderRadius: 4 }} />
        <Typography variant="caption" color="text.secondary" mt={1}>
          {completed} of {files.length} processed
        </Typography>
      </Box>

      <List dense>
        {files.map(file => {
          const status = statuses[file.fileId] || 'pending'
          return (
            <ListItem key={file.fileId}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {status === 'done' && <MdCheckCircle size={24} color="#4caf50" />}
                {status === 'error' && <MdError size={24} color="#f44336" />}
                {status === 'processing' && <MdHourglassEmpty size={24} color="#ff9800" />}
                {status === 'pending' && <MdHourglassEmpty size={24} color="#9e9e9e" />}
              </ListItemIcon>
              <ListItemText
                primary={file.fileName}
                secondary={
                  status === 'processing'
                    ? 'Analyzing...'
                    : status === 'done'
                    ? 'Complete'
                    : status === 'error'
                    ? 'Failed'
                    : 'Waiting...'
                }
              />
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}
